import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../api/auth';

interface User {
  id: string;
  username: string;
  email: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string, redirectPath?: string) => Promise<any>;
  logout: () => Promise<void>;
  checkAndRedirectIfAuthenticated: (publicRoutes?: boolean) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Public routes that don't need redirection if authenticated
const PUBLIC_ROUTES = ['/', '/signin', '/signup'];

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to check auth status
  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Verify authentication with the backend
      try {
        // First make a light auth verification call
        const isLoggedIn = await authApi.isLoggedIn();
        
        if (!isLoggedIn) {
          console.log('Server verification failed, user is not authenticated');
          setUser(null);
          setIsAuthenticated(false);
          return false;
        }
        
        // If server verification succeeds, get user data
        // We already verified authentication with /user/me via isLoggedIn
        // We can use the same result to get user data when possible
        try {
          const userData = await authApi.getCurrentUser();
          console.log('User authenticated successfully');
          setUser(userData);
          setIsAuthenticated(true);
          return true;
        } catch (userError) {
          // If we can't get user data but auth succeeded,
          // we're still authenticated but with limited user info
          console.warn('Auth succeeded but could not fetch user data:', userError);
          setIsAuthenticated(true);
          return true;
        }
      } catch (error) {
        // If there's an API error, user is not authenticated
        console.log('Auth check failed, user not authenticated:', error);
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      // If there's any other error, user is not authenticated
      console.log('Auth check error:', error);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Check if user is already authenticated on initial mount 
  useEffect(() => {
    const initialAuthCheck = async () => {
      const authenticated = await checkAuthStatus();
      
      // Only redirect on mount if authenticated and on a public route
      if (authenticated && PUBLIC_ROUTES.includes(location.pathname)) {
        navigate('/dashboard');
      }
    };
    
    initialAuthCheck();
  }, []);
  
  // Set up a refresh token interval only when authenticated
  useEffect(() => {
    // Only set up interval if user is authenticated
    if (!isAuthenticated) return;
    
    console.log('Setting up token refresh interval');
    
    // Calculate refresh time based on token expiration
    // Refresh at 80% of token lifetime to ensure we always have valid tokens
    const refreshInterval = setInterval(async () => {
      try {
        // Only refresh if we're authenticated - avoid extra API calls
        await authApi.isLoggedIn();
      } catch (error) {
        console.error('Background token refresh failed:', error);
      }
    }, 20 * 60 * 1000); // 20 minutes is a safer interval
    
    // Clean up interval on unmount or when auth state changes
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  // Function to check if user is authenticated and redirect from public pages if needed
  const checkAndRedirectIfAuthenticated = async (publicRoutesOnly = true): Promise<boolean> => {
    // First, try to use cached state to avoid API calls when possible
    
    // Case 1: We're already authenticated and state is loaded
    if (!loading && isAuthenticated) {
      // If on a public route and we're checking public routes, redirect to dashboard
      if (publicRoutesOnly && PUBLIC_ROUTES.includes(location.pathname)) {
        navigate('/dashboard');
      }
      return true;
    }
    
    // Case 2: We're already confirmed not authenticated and state is loaded
    if (!loading && !isAuthenticated) {
      // If not on a public route, redirect to login
      if (!PUBLIC_ROUTES.includes(location.pathname)) {
        navigate('/signin', { state: { from: location.pathname } });
      }
      return false;
    }
    
    // Case 3: Auth state unknown or loading - need to check with backend
    console.log('Auth state unknown, checking with backend');
    
    // Use a single API call to determine auth state
    const authenticated = await checkAuthStatus();
    
    // Handle redirects based on authentication result
    
    // If authenticated and on a public route (and we care about public routes), redirect to dashboard
    if (authenticated && publicRoutesOnly && PUBLIC_ROUTES.includes(location.pathname)) {
      navigate('/dashboard');
    }
    
    // If not authenticated and not on a public route, redirect to signin
    if (!authenticated && !PUBLIC_ROUTES.includes(location.pathname)) {
      navigate('/signin', { state: { from: location.pathname } });
    }
    
    return authenticated;
  };

  const login = async (username: string, password: string, redirectPath?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Call the login API
      const response = await authApi.login(username, password);
      
      // Get user data
      try {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
      } catch (userError) {
        console.error('Failed to fetch user data:', userError);
      }
      
      setIsAuthenticated(true);
      
      // Navigate to either the provided redirectPath or dashboard
      navigate(redirectPath || '/dashboard');
      
      return response; // Return response on success
    } catch (err) {
      // Handle the error and re-throw
      console.error('Login error:', err);
      setError('Invalid username or password');
      setIsAuthenticated(false);
      setUser(null);
      throw err; // Re-throw to let the component handle it
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      
      // First update local state
      setUser(null);
      setIsAuthenticated(false);
      
      // Use our clearAuthState method to ensure consistent cookie clearing
      authApi.clearAuthState();
      
      // Call the API to invalidate the session on the server
      try {
        await authApi.logout();
      } catch (logoutErr) {
        console.error('Logout API call failed:', logoutErr);
        // Continue with client-side logout even if API call fails
      }
      
      // Navigate to signin page after all state is cleared
      navigate('/signin');
    } catch (err) {
      console.error('Logout process failed:', err);
      
      // Ensure state is cleared even in case of errors
      setUser(null);
      setIsAuthenticated(false);
      authApi.clearAuthState();
      
      navigate('/signin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      loading, 
      error, 
      login, 
      logout,
      checkAndRedirectIfAuthenticated
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}